import { UserPlus, Warning, Prohibit, ShieldCheck } from "@phosphor-icons/react";
import LoaderLoop from "../../../shared/loader/LoaderLoop";
import { HEBREW_DAYS_FULL, formatHebrewDate } from "../../../../utils/dateUtils";
import "./UnassignedPatientPanel.css";

/**
 * E6 — the selected patient is not assigned to the appointment's therapist
 * (assigned patient-sharing mode, booking for someone else). Mirrors
 * RoomBlockedPanel / TherapistConflictPanel: reason line + one-click fix.
 *
 *  - allowMultipleTherapists (and the booker may reassign): "שייך/י את T
 *    למטופל/ת" calls the Cloud Function and clears the gate locally.
 *  - admins / `calendar.manageOthersAppointments` holders may always
 *    "קבע/י בכל זאת" (local acknowledgement, no assignment) — in legacy
 *    single-therapist clinics this is their only way through.
 *  - otherwise: hard block — the patient must be assigned first.
 *
 * Series: each occurrence with a per-row therapist override that is not
 * assigned gets its own line + assign action.
 */
function UnassignedPatientPanel({ form }) {
  const {
    unassignedTherapistForPatient,
    seriesUnassignedIdx = [],
    seriesPreview,
    seriesOverrides,
    allowMultipleTherapists,
    canAssignTherapistToPatient,
    assignTherapistToPatient,
    isAssigningTherapist,
    therapistDisplayName,
    canAcknowledgeUnassigned,
    unassignedAcknowledged,
    setUnassignedAcknowledged,
  } = form;

  const hasBase = !!unassignedTherapistForPatient;
  const hasSeries = seriesUnassignedIdx.length > 0;
  if (!hasBase && !hasSeries) return null;

  const acknowledged = canAcknowledgeUnassigned && unassignedAcknowledged;

  const blockedCopy = !allowMultipleTherapists
    ? "יש לשייך את המטופל/ת למטפל/ת לפני קביעת התור"
    : "רק מנהל/ת או מי שהורשה/תה להעביר מטופלים יכול/ה לשייך. פנה/י למנהל/ת הקליניקה.";

  const renderAction = (tid, name) => {
    if (acknowledged) return null;
    if (canAssignTherapistToPatient) {
      return (
        <button
          type="button"
          className="apt-wiz-gate-btn upp-assign-btn"
          onClick={() => assignTherapistToPatient(tid)}
          disabled={isAssigningTherapist}
        >
          {isAssigningTherapist ? (
            <LoaderLoop size="xs" />
          ) : (
            <UserPlus size={13} weight="bold" />
          )}{" "}
          {isAssigningTherapist ? "משייך/ת…" : `שייך/י את ${name} למטופל/ת`}
        </button>
      );
    }
    // The acknowledge action lives once in the panel footer.
    if (canAcknowledgeUnassigned) return null;
    return (
      <span className="upp-blocked">
        <Prohibit size={14} weight="bold" />
        {blockedCopy}
      </span>
    );
  };

  return (
    <div className={`upp-panel ${acknowledged ? "overridden" : ""}`} role="status">
      {hasBase && (
        <>
          <div className="upp-reason">
            <Warning size={16} weight="fill" />
            <span>
              המטופל/ת אינו/ה משויך/ת ל{unassignedTherapistForPatient.name}
            </span>
          </div>
          <div className="upp-actions">
            {renderAction(
              unassignedTherapistForPatient.id,
              unassignedTherapistForPatient.name
            )}
          </div>
        </>
      )}

      {hasSeries && (
        <div className="upp-series">
          <div className="upp-reason">
            <Warning size={16} weight="fill" />
            <span>
              {seriesUnassignedIdx.length === 1
                ? "באחד המפגשים המטפל/ת אינו/ה משויך/ת למטופל/ת"
                : `ב-${seriesUnassignedIdx.length} מפגשים המטפל/ת אינו/ה משויך/ת למטופל/ת`}
            </span>
          </div>
          <ul className="upp-series-list">
            {seriesUnassignedIdx.map((idx) => {
              const item = seriesPreview?.items?.[idx];
              const tid = seriesOverrides?.[idx]?.therapistId;
              const name = therapistDisplayName(tid) || "המטפל/ת";
              return (
                <li key={idx} className="upp-series-row">
                  <span className="upp-series-when">
                    {item ? (
                      <>
                        מפגש {item.seriesIndex} · יום{" "}
                        {HEBREW_DAYS_FULL[item.date.getDay()]}{" "}
                        {formatHebrewDate(item.date, "dd/MM")} · {item.time}
                      </>
                    ) : (
                      `מפגש ${idx + 1}`
                    )}
                    {" — "}
                    {name}
                  </span>
                  {renderAction(tid, name)}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {canAcknowledgeUnassigned &&
        (acknowledged ? (
          <div className="upp-override-note">
            <ShieldCheck size={16} weight="fill" />
            <span>אושר — התור ייקבע ללא שיוך המטופל/ת למטפל/ת.</span>
          </div>
        ) : (
          <div className="upp-actions upp-footer">
            <button
              type="button"
              className="apt-wiz-gate-btn"
              onClick={() => setUnassignedAcknowledged(true)}
              disabled={isAssigningTherapist}
            >
              קבע/י בכל זאת
            </button>
            <span className="upp-hint">
              {canAssignTherapistToPatient
                ? "או קבע/י ללא שיוך — המטופל/ת לא יופיע/תופיע ברשימת המטפל/ת."
                : "התור ייקבע ללא שיוך — המטופל/ת לא יופיע/תופיע ברשימת המטפל/ת."}
            </span>
          </div>
        ))}
    </div>
  );
}

export default UnassignedPatientPanel;
